// KDS.js - Versión Mejorada con Diseño Homogéneo + Biblioteca REFERENCIA
import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import PropTypes from "prop-types";
import { useProperty } from "../context/PropertyContext";
import enviar_pedidokds from "../utils/enviar_pedidokds";
import { IP } from '../configuracion.js';
import "../styles/KDS.css";
import "../styles/KDSTiempos.css"; // ── Tiempos de Servicio (aislado)
import KDSBiblioteca from "./KDS_biblioteca.js";
import {
  ChefHat,
  BookOpen,
  RotateCcw,
  RefreshCw,
  AlertTriangle,
  Clock3,
  Users,
  ClipboardList,
  CheckCircle2,
  XCircle,
  History,
  ArrowLeft,
  Search,
  Printer,
  Maximize2,
  Minimize2,
  Coffee,
  UtensilsCrossed,
  Timer,
  Bell,
  Library,
  Volume2,
  VolumeX
} from "lucide-react";

// ============================================
// CONSTANTES Y HELPERS
// ============================================
const API_BASE = `${IP}/tavpos/backend`;
const POLLING_INTERVAL = 15000;

// Genera un beep con Web Audio API (sin URL externa)
const playBeep = (frequency = 880, duration = 0.25, volume = 0.35) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration);
    // Cerrar contexto después para liberar recursos
    osc.onended = () => { try { ctx.close(); } catch {} };
  } catch (e) {
    console.log('Audio error:', e);
  }
};

// Beep doble para nueva orden (más llamativo)
const playNewOrderBeep = () => {
  playBeep(880, 0.15, 0.4);
  setTimeout(() => playBeep(1100, 0.2, 0.4), 180);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
};

const formatTimeAgo = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  
  if (minutes < 1) return `${seconds} seg`;
  if (minutes < 60) return `${minutes} min ${seconds} seg`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const getTimerStatus = (dateString) => {
  if (!dateString) return 'normal';
  const diff = Date.now() - new Date(dateString).getTime();
  const minutes = diff / 60000;
  if (minutes >= 30) return 'critical';
  if (minutes >= 15) return 'warning';
  return 'normal';
};

const decodeUnicode = (str) => {
  if (!str) return "";
  try {
    return str.replace(/\\u[\dA-Fa-f]{4}/g, (match) =>
      String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16))
    );
  } catch {
    return str;
  }
};

// ============================================
// TIEMPOS DE SERVICIO — HELPERS
// ============================================

/**
 * Extrae el label de tiempo de los modificadores de un ítem KDS.
 * El POS envía modificadores como { nombre: "Tiempo 1" } (campo 'nombre' en el backend).
 * Retorna "Tiempo 1", "Tiempo 2", etc., o null si el ítem no tiene tiempo asignado.
 */
const getTiempoFromItem = (item) => {
  if (!item?.modificadores?.length) return null;
  const mod = item.modificadores.find((m) =>
    /^Tiempo\s+\d+$/i.test(m.nombre || m.name || "")
  );
  if (!mod) return null;
  return mod.nombre || mod.name || null;
};

/**
 * Convierte el array de órdenes pendientes en "tiempo-cards":
 *
 * • Órdenes SIN tiempos → pasan sin cambio (tiempoLabel: null, tiempoActive: true).
 *   El comportamiento es exactamente el actual.
 *
 * • Órdenes CON tiempos → se dividen en sub-cards, una por tiempo distinto.
 *   - Sub-card[0] → tiempoActive: true  (primera en la secuencia, contador activo)
 *   - Sub-card[1+] → tiempoWaiting: true (bloqueadas, sin contador, sin interacción)
 *
 * Cuando el cocinero produce todos los ítems del tiempo activo, esa sub-card
 * desaparece del array y la siguiente sub-card se convierte automáticamente en
 * idx===0 → tiempoActive: true. La activación es derivada, no guardada en estado.
 *
 * @param {Array} pendingOrders - Órdenes del hook de polling
 * @returns {Array} Tiempo-cards aplanadas y ordenadas
 */
const buildTiempoOrders = (pendingOrders) => {
  const result = [];

  pendingOrders.forEach((order) => {
    const hasTiempos = order.items.some((item) => getTiempoFromItem(item));

    // Sin tiempos → comportamiento idéntico al actual
    if (!hasTiempos) {
      result.push({ ...order, tiempoLabel: null, tiempoActive: true, tiempoWaiting: false });
      return;
    }

    // Agrupar ítems por su tiempo
    const tiempoMap = new Map();
    order.items.forEach((item) => {
      const t = getTiempoFromItem(item) || "Tiempo 1";
      if (!tiempoMap.has(t)) tiempoMap.set(t, []);
      tiempoMap.get(t).push(item);
    });

    // Ordenar tiempos numéricamente (Tiempo 1 < Tiempo 2 < ...)
    const sortedTiempos = Array.from(tiempoMap.keys()).sort((a, b) => {
      const na = parseInt(a.match(/\d+/)?.[0] ?? "1", 10);
      const nb = parseInt(b.match(/\d+/)?.[0] ?? "1", 10);
      return na - nb;
    });

    // Crear una sub-card por tiempo
    sortedTiempos.forEach((t, idx) => {
      const num = parseInt(t.match(/\d+/)?.[0] ?? "1", 10);
      result.push({
        ...order,
        // ID único para esta sub-card (el id_pedido original + sufijo _tN)
        id: `${order.id}_t${num}`,
        originalOrderId: order.id,   // referencia al pedido real
        tiempoLabel: t,               // "Tiempo 1", "Tiempo 2", …
        tiempoIndex: num,             // número ordinal
        tiempoShort: `T${num}`,       // "T1", "T2", … (para mostrar)
        tiempoActive: idx === 0,      // solo la primera sub-card restante está activa
        tiempoWaiting: idx > 0,       // las demás están en espera
        tiempoTotal: sortedTiempos.length,
        items: tiempoMap.get(t),      // solo los ítems de este tiempo
      });
    });
  });

  return result;
};

// ============================================
// CUSTOM HOOKS
// ============================================

// Hook para manejar polling de órdenes
const useOrdersPolling = (property, kdsId, onNewOrders, soundEnabledRef) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);
  const previousCountRef = useRef(0);
  const hasLoadedOnce = useRef(false);

  const loadOrders = useCallback(async () => {
    if (!property) return;

    // AbortController: si getpedidos.php no responde en 8s, falla limpiamente
    // evita que el fetch cuelgue bloqueando el botón Recargar y el polling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}/getpedidos.php?property=${encodeURIComponent(property)}`,
        { signal: controller.signal }
      );
      if (!response.ok) throw new Error(`Error ${response.status}`);
      
      const data = await response.json();
      if (!data.success || !Array.isArray(data.data)) throw new Error("Formato inválido");

      const pendingList = [];
      const producedList = [];
      const voidList = [];

      data.data.forEach((cheque) => {
        cheque.pedidos?.forEach((pedido) => {
          (pedido.productos || []).forEach((prod) => {
            if (prod.KDS !== kdsId) return;
            
            const estado = prod.estado || prod.status || "";
            const baseItem = {
              ...prod,
              Iditem: prod.Iditem,
              mesa: cheque.mesa,
              cliente: cheque.cliente,
              vendedor: cheque.nombre_vendedor,
              revenue: cheque.revenue,
              numero_pedido: pedido.numero_pedido,
              id_pedido: pedido.id_pedido,
              date_proceso: prod.odate_proceso || cheque.fecha_cheque || new Date().toISOString()
            };

            if (estado === "pendiente") {
              pendingList.push(baseItem);
            } else if (estado === "Producido") {
              producedList.push({ ...baseItem, estado: "Producido", fecha_proceso: prod.odate_proceso || new Date().toLocaleString() });
            } else if (estado === "void") {
              voidList.push({ ...baseItem, void_name: prod.void_name || "Cancelado", fecha_cancelacion: prod.odate_proceso || new Date().toISOString() });
            }
          });
        });
      });

      // Agrupar pendientes por pedido
      const ordersMap = new Map();
      pendingList.forEach(item => {
        if (!ordersMap.has(item.id_pedido)) {
          ordersMap.set(item.id_pedido, {
            id: item.id_pedido,
            numero_pedido: item.numero_pedido,
            date_proceso: item.date_proceso,
            vendedor: item.vendedor,
            cliente: item.cliente,
            mesa: item.mesa,
            revenue: item.revenue,
            items: []
          });
        }
        ordersMap.get(item.id_pedido).items.push(item);
      });

      const pendingOrders = Array.from(ordersMap.values());
      const sortedVoids = voidList.sort((a, b) => new Date(b.fecha_cancelacion) - new Date(a.fecha_cancelacion)).slice(0, 10);

      // Notificar nuevas órdenes
      const newCount = pendingList.length;
      if (previousCountRef.current > 0 && newCount > previousCountRef.current) {
        if (!soundEnabledRef || soundEnabledRef.current !== false) {
          playNewOrderBeep();
        }
        // Pasar delta Y lista actualizada — handleNewOrders necesita ambos
        // para anunciar total y orden más antigua sin leer estado atrasado
        if (onNewOrders) onNewOrders(newCount - previousCountRef.current, pendingOrders);
      }
      previousCountRef.current = newCount;

      setLastUpdate(new Date());
      setError(null); // limpiar error previo en éxito
      hasLoadedOnce.current = true;
      return { pendingOrders, producedItems: producedList.reverse(), voidItems: sortedVoids };

    } catch (error) {
      // AbortError = timeout de 8s — no mostrar error al usuario (fallo transitorio de red)
      if (error.name !== 'AbortError') {
        console.error("Error loading orders:", error);
        // Solo mostrar error si nunca hubo una carga exitosa (arranque sin conexión)
        if (!hasLoadedOnce.current) {
          setError(error.message);
        }
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [property, kdsId, onNewOrders]);

  const fetchAndUpdate = useCallback(async () => {
    const result = await loadOrders();
    if (result && onNewOrders) onNewOrders(result);
  }, [loadOrders, onNewOrders]);

  useEffect(() => {
    if (!property) return;

    fetchAndUpdate();
    intervalRef.current = setInterval(fetchAndUpdate, POLLING_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [property, fetchAndUpdate]);

  return { loading, error, lastUpdate, refetch: fetchAndUpdate };
};

// Hook para navegación por teclado
const useKeyboardNavigation = (items, onSelect, onAction, enabled = true) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    if (!enabled || items.length === 0) return;
    
    const handleKeyDown = (e) => {
      const key = e.key;
      
      if (key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev <= 0 ? items.length - 1 : prev - 1;
          onSelect?.(items[newIndex]);
          return newIndex;
        });
      } else if (key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev >= items.length - 1 ? 0 : prev + 1;
          onSelect?.(items[newIndex]);
          return newIndex;
        });
      } else if (key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        onAction?.(items[selectedIndex]);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [items, selectedIndex, onSelect, onAction, enabled]);

  return { selectedIndex, setSelectedIndex };
};

// ============================================
// COMPONENTES OPTIMIZADOS
// ============================================

// Timer Component - Profesional
const OrderTimer = memo(({ startTime }) => {
  const [time, setTime] = useState(() => formatTimeAgo(startTime));
  const status = getTimerStatus(startTime);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(formatTimeAgo(startTime));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);
  
  const getStatusClass = () => {
    if (status === 'critical') return 'timer-critical pulse-critical';
    if (status === 'warning') return 'timer-warning pulse-warning';
    return 'timer-normal';
  };
  
  return (
    <div className={`order-timer ${getStatusClass()}`}>
      <Timer size={14} />
      <span className="timer-value">{time || '—'}</span>
    </div>
  );
});

OrderTimer.displayName = 'OrderTimer';

// Recipe Parser optimizado
const RecipeParser = {
  parse: (text) => {
    const lines = text.split('\n');
    const recipe = {
      nombre: '', descripcion: '', tiempo_preparacion: '', porciones: '',
      ingredientes: [], preparacion: [], tips: '', categoria: '', nivel: ''
    };
    
    let currentSection = '';
    
    for (const line of lines) {
      let trimmed = line.trim();
      if (!trimmed) continue;
      trimmed = decodeUnicode(trimmed);
      
      if (trimmed.startsWith('RECETA:')) recipe.nombre = trimmed.replace('RECETA:', '').trim();
      else if (trimmed.startsWith('DESCRIPCION:')) recipe.descripcion = trimmed.replace('DESCRIPCION:', '').trim();
      else if (trimmed.startsWith('TIEMPO_PREPARACION:')) recipe.tiempo_preparacion = trimmed.replace('TIEMPO_PREPARACION:', '').trim();
      else if (trimmed.startsWith('PORCIONES:')) recipe.porciones = trimmed.replace('PORCIONES:', '').trim();
      else if (trimmed === 'INGREDIENTES:') currentSection = 'ingredientes';
      else if (trimmed === 'PREPARACION:') currentSection = 'preparacion';
      else if (trimmed === 'TIPS:') currentSection = 'tips';
      else if (trimmed.startsWith('CATEGORIA:')) recipe.categoria = trimmed.replace('CATEGORIA:', '').trim();
      else if (trimmed.startsWith('NIVEL:')) recipe.nivel = trimmed.replace('NIVEL:', '').trim();
      else if (currentSection === 'ingredientes' && trimmed.startsWith('-')) {
        recipe.ingredientes.push(trimmed.replace('-', '').trim());
      } else if (currentSection === 'preparacion' && /^\d+\./.test(trimmed)) {
        recipe.preparacion.push(trimmed.replace(/^\d+\.\s*/, '').trim());
      } else if (currentSection === 'tips' && trimmed) {
        recipe.tips += (recipe.tips ? ' ' : '') + trimmed;
      }
    }
    return recipe;
  }
};

// RecipePanel - Mejorado con texto blanco y mayor tamaño
const RecipePanel = memo(({ productId, productName, onClose, property }) => {
  const [recipeData, setRecipeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (!productId || !property) return;
    
    const loadRecipe = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const baseUrl = `${API_BASE}/data/${property}/recetas/`;
        const txtResponse = await fetch(`${baseUrl}${productId}.txt`);
        if (!txtResponse.ok) throw new Error(`No se encontró receta para ID: ${productId}`);
        
        const txtContent = await txtResponse.text();
        setRecipeData(RecipeParser.parse(txtContent));
        
        // Intentar cargar imagen
        const imgUrl = `${baseUrl}${productId}.webp`;
        const imgTest = new Image();
        imgTest.onload = () => setImageUrl(imgUrl);
        imgTest.onerror = () => setImageUrl(null);
        imgTest.src = imgUrl;
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadRecipe();
  }, [productId, property]);

  if (!productId) return null;

  return (
    <div className="recipe-panel-overlay" onClick={onClose}>
      <div className="recipe-panel" onClick={(e) => e.stopPropagation()}>
        <div className="recipe-panel-header">
          <div className="recipe-title">
            <BookOpen size={24} />
            <h3>{decodeUnicode(productName)} - Receta</h3>
          </div>
          <button onClick={onClose} className="btn-close-recipe">
            <XCircle size={18} /> Cerrar
          </button>
        </div>
        
        <div className="recipe-panel-body">
          {loading && (
            <div className="recipe-loading">
              <div className="spinner"></div>
              <p>Cargando receta...</p>
            </div>
          )}
          
          {error && (
            <div className="recipe-error">
              <AlertTriangle size={32} />
              <p>{error}</p>
              <small>Asegúrate que exista {productId}.txt en la carpeta de recetas</small>
            </div>
          )}
          
          {recipeData && !loading && (
            <>
              <div className="recipe-content-modern">
                <div className="recipe-meta-grid">
                  <div className="meta-card">
                    <Clock3 size={18} />
                    <span className="meta-label">Tiempo</span>
                    <span className="meta-value">{recipeData.tiempo_preparacion || 'N/E'}</span>
                  </div>
                  <div className="meta-card">
                    <Users size={18} />
                    <span className="meta-label">Porciones</span>
                    <span className="meta-value">{recipeData.porciones || 'N/E'}</span>
                  </div>
                  <div className="meta-card">
                    <ChefHat size={18} />
                    <span className="meta-label">Nivel</span>
                    <span className="meta-value">{recipeData.nivel || 'Básico'}</span>
                  </div>
                </div>
                
                {recipeData.descripcion && (
                  <div className="recipe-section">
                    <h4>📝 Descripción</h4>
                    <p>{recipeData.descripcion}</p>
                  </div>
                )}
                
                {recipeData.ingredientes.length > 0 && (
                  <div className="recipe-section">
                    <h4>🛒 Ingredientes</h4>
                    <ul className="ingredients-list">
                      {recipeData.ingredientes.map((ing, idx) => (
                        <li key={idx}>{ing}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {recipeData.preparacion.length > 0 && (
                  <div className="recipe-section">
                    <h4>👨‍🍳 Preparación</h4>
                    <ol className="preparation-list">
                      {recipeData.preparacion.map((step, idx) => (
                        <li key={idx}>{step}</li>
                      ))}
                    </ol>
                  </div>
                )}
                
                {recipeData.tips && (
                  <div className="recipe-section tips-section">
                    <h4>💡 Tips del Chef</h4>
                    <p>{recipeData.tips}</p>
                  </div>
                )}
              </div>
              
              {imageUrl && (
                <div className="recipe-image-sidebar">
                  <img src={imageUrl} alt={recipeData?.nombre} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

RecipePanel.displayName = 'RecipePanel';

// OrderItem - Optimizado con memo (VERDE para pendientes)
const OrderItem = memo(({ item, isSelected, isFocused, onSelect, onShowRecipe, orderId, itemId }) => {
  const cantidad = Math.abs(Number(item.cantidad) || 1);
  const precio = Number(item.precio) || 0;
  const consumo = precio * cantidad;
  const descuento = Math.min(Math.max(Number(item.monto_descuento) || 0, 0), consumo);
  const showDiscount = descuento > 0;

  const handleClick = () => onSelect(orderId, itemId);
  const handleRecipe = (e) => {
    e.stopPropagation();
    onShowRecipe(item.Iditem, item.nombre);
  };

  return (
    <div
      className={`order-item ${isSelected ? 'selected' : ''} ${isFocused ? 'kb-focused' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick(e)}
    >
      <div className="item-main-info">
        <span className="item-quantity">{cantidad}x</span>
        <span className="item-name">{decodeUnicode(item.nombre)}</span>
        {showDiscount && <span className="item-discount">-${descuento.toFixed(2)}</span>}
        <span className="item-total">${(consumo - descuento).toFixed(2)}</span>
        <button className="btn-recipe-mini" onClick={handleRecipe} title="Ver receta">
          <BookOpen size={16} />
        </button>
      </div>
      
      {item.modificadores?.length > 0 && (
        <div className="item-modifiers">
          {item.modificadores.map((mod, idx) => (
            <span key={idx} className="modifier-badge">
              + {decodeUnicode(mod.nombre)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
});

OrderItem.displayName = 'OrderItem';

// OrderCard - Optimizado (con soporte para Tiempos de Servicio + multi-select)
const OrderCard = memo(({ order, selectedProductIds, kbFocusedProductId, onSelectProduct, onShowRecipe, orderStatus }) => {
  const timerStatus = getTimerStatus(order.date_proceso);

  // ── Tiempos de Servicio ────────────────────────────────────────────────
  const isWaiting = !!order.tiempoWaiting;       // tarjeta bloqueada (Tiempo 2+)
  const hastiempo = !!order.tiempoLabel;           // orden con sistema de tiempos
  const headerTimerClass = isWaiting ? 'waiting' : timerStatus;

  // La tarjeta se resalta si ALGUNO de sus ítems está seleccionado
  const isAnyItemSelected = !isWaiting && order.items.some(item => selectedProductIds.has(item.id_producto));

  return (
    <div
      className={[
        'order-card',
        isAnyItemSelected ? 'order-selected' : '',
        hastiempo && !isWaiting ? 'order-card-tiempo-active' : '',
        isWaiting ? 'order-card-tiempo-waiting' : '',
      ].filter(Boolean).join(' ')}
      data-order-status={orderStatus || "pending"}
    >
      <div className={`order-header order-header-${headerTimerClass}`}>
        <div className="order-info-left">
          <span className="order-revenue">{decodeUnicode(order.revenue || "Restaurante")}</span>
          <span className="order-clients">
            <Users size={14} /> {order.cliente || "1"}
          </span>
          <span className="order-waiter">{decodeUnicode(order.vendedor || "Sistema")}</span>
        </div>

        <div className="order-info-center">
          <span className="order-number">
            Orden #{order.numero_pedido}
            {/* Badge de tiempo — solo visible en órdenes con tiempos */}
            {hastiempo && (
              <span className={`kds-tiempo-badge ${isWaiting ? 'kds-tiempo-badge-waiting' : 'kds-tiempo-badge-active'}`}>
                {order.tiempoShort}{isWaiting ? ' ⏸' : ' 🔥'}
              </span>
            )}
          </span>
        </div>

        <div className="order-info-right">
          <span className="order-table">Mesa {decodeUnicode(order.mesa || "—")}</span>
          {/* Tarjeta activa → timer normal | tarjeta en espera → etiqueta EN ESPERA */}
          {!isWaiting
            ? <OrderTimer startTime={order.date_proceso} />
            : <span className="kds-tiempo-waiting-label">EN ESPERA</span>
          }
        </div>
      </div>

      <div className={`order-items-container${isWaiting ? ' order-items-container-waiting' : ''}`}>
        {/* Banner informativo solo en tarjetas en espera */}
        {isWaiting && (
          <div className="kds-tiempo-waiting-banner">
            <span className="waiting-icon">⏸</span>
            <span>
              {order.tiempoLabel} — esperando que {
                order.tiempoIndex > 1 ? `Tiempo ${order.tiempoIndex - 1}` : 'el tiempo anterior'
              } se complete · {order.items.length} producto(s)
            </span>
          </div>
        )}

        {order.items.map((item) => (
          <OrderItem
            key={item.id_producto}
            item={item}
            isSelected={!isWaiting && selectedProductIds.has(item.id_producto)}
            isFocused={!isWaiting && kbFocusedProductId === item.id_producto}
            onSelect={isWaiting ? () => {} : onSelectProduct}
            onShowRecipe={isWaiting ? () => {} : onShowRecipe}
            orderId={order.id}
            itemId={item.id_producto}
          />
        ))}
      </div>
    </div>
  );
});

OrderCard.displayName = 'OrderCard';

// BumpBar - Mejorado profesionalmente con botón REFERENCIA + multi-select
const BumpBar = memo(({
  onProduce, onShowVoids, onShowHistory, onReload,
  onBackToPending, onShowRecipe, selectedItem, selectionCount,
  voidsCount, historyCount, viewMode, hasRecipe, onToggleFullscreen, isFullscreen,
  onShowLibrary, isProducing
}) => {
  const hasSelection = selectionCount > 0;
  const produceDisabled = viewMode !== 'pending' || !hasSelection || isProducing;

  return (
    <div className="bump-bar">
      <div className="bump-buttons">
        <button
          className={`bump-btn bump-btn-produce ${produceDisabled ? 'disabled' : ''} ${isProducing ? 'producing' : ''}`}
          onClick={() => !produceDisabled && onProduce()}
          disabled={produceDisabled}
        >
          {isProducing ? (
            <>
              <RefreshCw size={22} className="spin-icon" />
              <span className="bump-label">ENVIANDO…</span>
            </>
          ) : (
            <>
              <CheckCircle2 size={24} />
              <span className="bump-label">
                {selectionCount > 1 ? `PRODUCIR (${selectionCount})` : 'PRODUCIR'}
              </span>
            </>
          )}
          <span className="bump-hint">F1</span>
        </button>

        <button
          className={`bump-btn bump-btn-recipe ${(!hasSelection || selectionCount > 1 || viewMode !== 'pending') ? 'disabled' : ''}`}
          onClick={() => viewMode === 'pending' && selectionCount === 1 && selectedItem && onShowRecipe(selectedItem.Iditem, selectedItem.nombre)}
          disabled={viewMode !== 'pending' || selectionCount !== 1}
        >
          <BookOpen size={24} />
          <span className="bump-label">RECETA</span>
          <span className="bump-hint">F6</span>
        </button>
        
        {/* NUEVO BOTÓN REFERENCIA */}
        <button 
          className="bump-btn bump-btn-reference"
          onClick={onShowLibrary}
        >
          <Library size={24} />
          <span className="bump-label">REFERENCIA</span>
          <span className="bump-hint">F7</span>
        </button>
        
        <button className="bump-btn bump-btn-back" onClick={onBackToPending}>
          <ArrowLeft size={24} />
          <span className="bump-label">REGRESAR</span>
          <span className="bump-hint">F3</span>
        </button>
        
        <button className={`bump-btn bump-btn-voids ${viewMode === 'voids' ? 'active' : ''}`} onClick={onShowVoids}>
          <XCircle size={24} />
          <span className="bump-label">CANCELADOS</span>
          <span className="bump-badge">{voidsCount}</span>
          <span className="bump-hint">F2</span>
        </button>
        
        <button className={`bump-btn bump-btn-history ${viewMode === 'history' ? 'active' : ''}`} onClick={onShowHistory}>
          <History size={24} />
          <span className="bump-label">HISTORIAL</span>
          <span className="bump-badge">{historyCount}</span>
          <span className="bump-hint">F4</span>
        </button>
        
        <button className="bump-btn bump-btn-reload" onClick={onReload}>
          <RefreshCw size={24} />
          <span className="bump-label">RECARGAR</span>
          <span className="bump-hint">F5</span>
        </button>
        
        <button className="bump-btn bump-btn-fullscreen" onClick={onToggleFullscreen}>
          {isFullscreen ? <Minimize2 size={24} /> : <Maximize2 size={24} />}
          <span className="bump-label">PANTALLA</span>
          <span className="bump-hint">F11</span>
        </button>
      </div>
      
      {viewMode === 'pending' && hasSelection && (
        <div className="bump-selection">
          {selectionCount > 1 ? (
            <>
              <span>📌 <strong>{selectionCount} productos seleccionados</strong></span>
              <span className="bump-hint-text">ESC = deseleccionar · Ctrl+A = todos · ↑↓ + Espacio = teclado</span>
            </>
          ) : selectedItem ? (
            <>
              <span>📌 Seleccionado:</span>
              <strong>{decodeUnicode(selectedItem.nombre)}</strong>
              {hasRecipe && <span className="recipe-badge">📖 Receta disponible</span>}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
});

BumpBar.displayName = 'BumpBar';

// ============================================
// KDS PRINCIPAL - MEJORADO
// ============================================
const KDS = ({ kdsId }) => {
  const { property } = useProperty();
  const [pendingOrders, setPendingOrders] = useState([]);
  const [producedItems, setProducedItems] = useState([]);
  const [voidItems, setVoidItems] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedVoidId, setSelectedVoidId] = useState(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [viewMode, setViewMode] = useState("pending");
  const [showHistory, setShowHistory] = useState(false);
  const [showRecipe, setShowRecipe] = useState(false);
  const [recipeProduct, setRecipeProduct] = useState({ id: null, name: "" });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [serverStatus, setServerStatus] = useState("checking");
  const [showLibrary, setShowLibrary] = useState(false); // Estado para biblioteca
  const [isProducing, setIsProducing] = useState(false); // Visual feedback del botón Producir
  // ── Multi-select ──────────────────────────────────────────────────────────
  // Map<productId, {orderId, productId, item}> — vacío = nada seleccionado
  const [selectedItems, setSelectedItems] = useState(new Map());
  // Cursor de teclado: el productId que tiene el foco visual (flecha arriba/abajo)
  const [kbFocusedProductId, setKbFocusedProductId] = useState(null);
  // Filtro por revenue/centro de consumo (null = Todos)
  const [selectedRevenue, setSelectedRevenue] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem('kds_sound') !== 'false'; } catch { return true; }
  });

  const isProcessingRef = useRef(false); // conservado para revertVoid/revertProduced
  const processingQueueRef = useRef([]);   // cola de items pendientes de enviar al servidor
  const isQueueRunningRef = useRef(false); // ¿está el procesador de cola activo?
  // voiceQueueRef: array de { text: string, orderId?: string }
  // orderId presente → el mensaje se descarta si esa orden ya no está pendiente
  const voiceQueueRef = useRef([]);
  const isVoiceSpeakingRef = useRef(false); // ¿el sintetizador está hablando ahora mismo?
  const voiceWatchdogRef = useRef(null);    // timer de seguridad si onend no dispara (bug Chrome)
  const soundEnabledRef = useRef(soundEnabled);
  // Espejo mutable de pendingOrders — accesible desde callbacks estables (refs)
  const pendingOrdersRef = useRef([]);

  // Sync refs when state changes
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
    try { localStorage.setItem('kds_sound', soundEnabled ? 'true' : 'false'); } catch {}
  }, [soundEnabled]);

  useEffect(() => {
    pendingOrdersRef.current = pendingOrders;
  }, [pendingOrders]);

  // Procesador de cola de voz — robusto ante el bug de congelamiento de Chrome
  // Chrome congela speechSynthesis después de ~15s de inactividad y onend nunca dispara.
  // Solución: cancel() resetea el motor, delay de 50ms deja que Chrome procese el reset,
  // y el watchdog desbloquea la cola si onend sigue sin disparar.
  // Cada item de voiceQueueRef es { text, orderId? }.
  // Si orderId está presente, el item se descarta si esa orden ya no está pendiente.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const processVoiceQueue = useCallback(() => {
    if (isVoiceSpeakingRef.current) return;
    if (!window.speechSynthesis) return;

    // Avanzar la cola descartando mensajes obsoletos (orden ya producida)
    let textToSpeak = null;
    while (voiceQueueRef.current.length > 0) {
      const candidate = voiceQueueRef.current.shift();
      const { text, orderId } = typeof candidate === 'string'
        ? { text: candidate, orderId: null }
        : candidate;

      // Descarte de obsoletos: si el mensaje referenciaba una orden específica
      // y esa orden ya no está en la lista de pendientes → saltar
      if (orderId) {
        const stillPending = pendingOrdersRef.current.some(o => o.id === orderId);
        if (!stillPending) continue;
      }

      textToSpeak = text;
      break;
    }

    if (!textToSpeak) return; // cola vacía o todos los items eran obsoletos

    isVoiceSpeakingRef.current = true;

    // cancel() resetea el motor de Chrome sin cortar nada (la cola anterior ya terminó)
    window.speechSynthesis.cancel();

    setTimeout(() => {
      const utter = new SpeechSynthesisUtterance(textToSpeak);
      utter.lang = 'es-MX';
      utter.rate = 0.9;
      utter.volume = 1;

      const done = () => {
        if (voiceWatchdogRef.current) { clearTimeout(voiceWatchdogRef.current); voiceWatchdogRef.current = null; }
        isVoiceSpeakingRef.current = false;
        processVoiceQueue();
      };

      utter.onend  = done;
      utter.onerror = done;

      // Watchdog: si onend nunca dispara (Chrome bug), desbloquear la cola tras 12s
      voiceWatchdogRef.current = setTimeout(done, 12000);

      window.speechSynthesis.speak(utter);
    }, 50); // pausa mínima para que Chrome procese el cancel()
  }, []); // solo refs → función estable; auto-referencia intencional

  // speak — encola el mensaje sin interrumpir lo que se está reproduciendo
  // text    : texto a pronunciar
  // orderId : (opcional) si se pasa, el mensaje se descarta si esa orden ya fue producida
  const speak = useCallback((text, orderId) => {
    if (!soundEnabledRef.current) return;
    if (!window.speechSynthesis) return;
    // Deduplicación: no encolar si el mismo texto ya está en la cola
    const alreadyQueued = voiceQueueRef.current.some(item =>
      (typeof item === 'string' ? item : item.text) === text
    );
    if (alreadyQueued) return;
    voiceQueueRef.current.push(orderId ? { text, orderId } : { text });
    processVoiceQueue();
  }, [processVoiceQueue]);

  // Check server status — timeout 5s para evitar requests colgados indefinidamente
  const checkServerStatus = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(
        `${API_BASE}/status.php?property=${encodeURIComponent(property)}`,
        { signal: controller.signal }
      );
      const data = await response.json();
      setServerStatus(data.status === "ok" || data.status === "online" ? "online" : "offline");
    } catch (err) {
      // AbortError = timeout → no cambiar estado (evita parpadeo por latencia transitoria)
      if (err.name !== 'AbortError') setServerStatus("offline");
    } finally {
      clearTimeout(timeoutId);
    }
  }, [property]);

  // Load orders handler
  const handleOrdersLoaded = useCallback((result) => {
    if (result) {
      setPendingOrders(result.pendingOrders);
      setProducedItems(result.producedItems);
      setVoidItems(result.voidItems);
    }
  }, []);

  // ── Tiempos de Servicio: derivar tiempo-cards desde pendingOrders ─────────
  // Órdenes sin tiempos → pasan sin cambio (comportamiento actual intacto).
  // Órdenes con tiempos → se dividen en sub-cards; la primera es activa,
  // las demás están en espera hasta que la anterior se produce completamente.
  const tiempoOrders = useMemo(() => buildTiempoOrders(pendingOrders), [pendingOrders]);

  // ── Beep cuando un tiempo pasa de EN ESPERA → ACTIVO ─────────────────────
  // Detecta sub-cards que anteriormente eran "waiting" y ahora son "active".
  // No afecta la lógica de producción ni el sonido de nueva orden.
  const prevWaitingIdsRef = useRef(new Set());
  useEffect(() => {
    const nowWaiting = new Set(
      tiempoOrders.filter((o) => o.tiempoWaiting).map((o) => o.id)
    );
    const nowActive = new Set(
      tiempoOrders.filter((o) => !o.tiempoWaiting && o.tiempoLabel).map((o) => o.id)
    );

    // Si un ID que estaba en "waiting" ahora está en "active" → promoción
    let promoted = false;
    prevWaitingIdsRef.current.forEach((id) => {
      if (nowActive.has(id)) promoted = true;
    });

    if (promoted && soundEnabledRef.current) {
      // Beep doble descendente para distinguirlo del beep de nueva orden
      playBeep(880, 0.18, 0.35);
      setTimeout(() => playBeep(660, 0.22, 0.35), 210);
    }

    prevWaitingIdsRef.current = nowWaiting;
  }, [tiempoOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set derivado de productIds seleccionados — pasado a OrderCard para isSelected por ítem
  const selectedProductIds = useMemo(() => new Set(selectedItems.keys()), [selectedItems]);

  // Revenues únicos presentes en las órdenes pendientes (para las pills de filtro)
  const availableRevenues = useMemo(() => {
    const seen = new Set();
    pendingOrders.forEach(o => seen.add(decodeUnicode(o.revenue || "Restaurante")));
    return Array.from(seen).sort();
  }, [pendingOrders]);

  // Conteo de ítems pendientes por revenue (para el badge de cada pill)
  const revenueCounts = useMemo(() => {
    const counts = {};
    pendingOrders.forEach(o => {
      const rev = decodeUnicode(o.revenue || "Restaurante");
      counts[rev] = (counts[rev] || 0) + o.items.length;
    });
    return counts;
  }, [pendingOrders]);

  // tiempoOrders filtradas por revenue seleccionado (null = Todos)
  const filteredTiempoOrders = useMemo(() => {
    if (!selectedRevenue) return tiempoOrders;
    return tiempoOrders.filter(o =>
      decodeUnicode(o.revenue || "Restaurante") === selectedRevenue
    );
  }, [tiempoOrders, selectedRevenue]);

  // Lista plana de ítems interactuables para navegación por teclado (respeta filtro de revenue)
  // IMPORTANTE: debe estar DESPUÉS de filteredTiempoOrders
  const navigableItems = useMemo(() =>
    filteredTiempoOrders
      .filter(o => !o.tiempoWaiting)
      .flatMap(o => o.items.map(item => ({ item, orderId: o.id })))
  , [filteredTiempoOrders]);

  // Limpiar selección cuando cambia el filtro de revenue
  useEffect(() => {
    setSelectedItems(new Map());
    setKbFocusedProductId(null);
  }, [selectedRevenue]);

  // Limpiar selectedItems/cursor si los productos ya no están en pendingOrders (después de polling)
  useEffect(() => {
    if (selectedItems.size === 0 && kbFocusedProductId === null) return;
    const allPids = new Set(pendingOrders.flatMap(o => o.items.map(i => i.id_producto)));
    setSelectedItems(prev => {
      if (prev.size === 0) return prev;
      const cleaned = new Map([...prev].filter(([pid]) => allPids.has(pid)));
      return cleaned.size === prev.size ? prev : cleaned;
    });
    if (kbFocusedProductId && !allPids.has(kbFocusedProductId)) {
      setKbFocusedProductId(null);
    }
  }, [pendingOrders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle new orders notification
  // count            : cuántas órdenes nuevas llegaron (delta)
  // newPendingOrders : lista completa actualizada de órdenes pendientes
  const handleNewOrders = useCallback((count, newPendingOrders) => {
    setNotificationCount(prev => prev + count);
    setTimeout(() => setNotificationCount(0), 3000);

    // 1. Anuncio de nuevas órdenes
    if (count === 1) speak('Nuevo pedido recibido');
    else if (count === 2) speak('Se recibieron dos nuevos pedidos');
    else if (count === 3) speak('Se recibieron tres nuevos pedidos');
    else speak(`Se recibieron ${count} nuevos pedidos`);

    // 2. Total de órdenes en espera (sin orderId — es info agregada, no de una orden específica)
    const orders = newPendingOrders || pendingOrdersRef.current;
    const total = orders.length;
    if (total > 0) {
      speak(`Hay ${total} ${total === 1 ? 'orden en espera' : 'órdenes en espera'}`);

      // 3. Orden más antigua — con orderId para que se descarte si se produce antes de hablar
      const oldest = orders.reduce((a, b) =>
        new Date(a.date_proceso) < new Date(b.date_proceso) ? a : b
      );
      const mins = Math.floor((Date.now() - new Date(oldest.date_proceso).getTime()) / 60000);
      if (mins > 0) {
        const mesa = oldest.mesa ? `Mesa ${oldest.mesa}` : `Pedido ${oldest.numero_pedido}`;
        speak(`La orden de ${mesa} lleva ${mins} ${mins === 1 ? 'minuto' : 'minutos'} en espera`, oldest.id);
      }
    }
  }, [speak]);

  // Use polling hook
  // Bug fix: el hook llama al mismo callback con (count:number) para notificación
  // Y con (result:object) para cargar órdenes. Hay que distinguir los dos casos
  // para evitar que handleOrdersLoaded reciba un número y limpie el estado.
  const { loading, error, refetch, lastUpdate } = useOrdersPolling(property, kdsId, (resultOrCount, pendingOrdersList) => {
    if (resultOrCount && typeof resultOrCount === 'object') {
      // Carga normal de órdenes
      handleOrdersLoaded(resultOrCount);
    } else if (typeof resultOrCount === 'number' && resultOrCount > 0) {
      // Notificación de nuevas órdenes — pasar también la lista actualizada
      handleNewOrders(resultOrCount, pendingOrdersList);
    }
  }, soundEnabledRef);

  // Fallback de conectividad: si el polling de órdenes respondió OK recientemente → online
  // Cubre el caso donde status.php falla pero getpedidos.php sí responde
  useEffect(() => {
    if (!lastUpdate) return;
    const age = Date.now() - lastUpdate.getTime();
    if (age < 20000) setServerStatus("online");
  }, [lastUpdate]);

  // Recordatorios periódicos inteligentes — anuncia solo cuando algo cambia
  // Lógica: cada 60s compara el estado actual contra lo último anunciado.
  // Solo habla si: cambió el total, cambió la orden más antigua, o la más antigua
  // cruzó un bloque de 10 minutos (0→10, 10→20, 20→30…).
  // Nunca repite el mismo mensaje. Descarta automáticamente mensajes obsoletos.
  const lastAnnouncedCountRef = useRef(-1);
  const lastAnnouncedOldestIdRef = useRef(null);
  const lastAnnouncedBucketRef = useRef(-1); // bloque de 10min ya anunciado

  useEffect(() => {
    const INTERVAL = 60 * 1000; // revisar cada minuto

    const announce = () => {
      if (!soundEnabledRef.current) return;

      if (pendingOrders.length === 0) {
        // Reiniciar estado para próxima vez que haya órdenes
        lastAnnouncedCountRef.current = 0;
        lastAnnouncedOldestIdRef.current = null;
        lastAnnouncedBucketRef.current = -1;
        return;
      }

      const now = Date.now();
      const total = pendingOrders.length;

      // Orden más antigua (mayor tiempo de espera = requiere mayor atención)
      const oldest = pendingOrders.reduce((a, b) =>
        new Date(a.date_proceso) < new Date(b.date_proceso) ? a : b
      );
      const oldestMins = Math.floor((now - new Date(oldest.date_proceso).getTime()) / 60000);
      const minutesBucket = Math.floor(oldestMins / 10); // cambia cada 10 minutos

      const countChanged  = total !== lastAnnouncedCountRef.current;
      const oldestChanged = oldest.id !== lastAnnouncedOldestIdRef.current;
      const bucketChanged = minutesBucket !== lastAnnouncedBucketRef.current;

      // Silencio si nada relevante cambió
      if (!countChanged && !oldestChanged && !bucketChanged) return;

      // Anunciar total (sin orderId — es info agregada)
      speak(`Hay ${total} ${total === 1 ? 'orden en espera' : 'órdenes en espera'}`);

      // Anunciar orden más antigua con orderId para descarte si se produce antes de hablar
      const mesa = oldest.mesa ? `Mesa ${oldest.mesa}` : `Pedido ${oldest.numero_pedido}`;
      const timeStr = oldestMins < 1
        ? 'menos de un minuto'
        : `${oldestMins} ${oldestMins === 1 ? 'minuto' : 'minutos'}`;
      speak(`La orden de ${mesa} lleva ${timeStr} en espera`, oldest.id);

      // Actualizar estado para evitar repetición
      lastAnnouncedCountRef.current = total;
      lastAnnouncedOldestIdRef.current = oldest.id;
      lastAnnouncedBucketRef.current = minutesBucket;
    };

    const interval = setInterval(announce, INTERVAL);
    return () => clearInterval(interval);
  }, [pendingOrders, speak]);

  // ── Aviso inmediato al pasar de 0 a 1+ pedidos pendientes ─────────────────
  // Efecto aditivo e independiente: NO modifica useOrdersPolling ni la
  // condición de nueva orden del polling (previousCountRef.current > 0).
  // Cubre el caso donde el KDS estaba vacío y llega el primer pedido, que
  // hoy no suena porque ese guardián bloquea justo esa transición.
  const hayPedidosPendientes = pendingOrders.length > 0;
  useEffect(() => {
    if (hayPedidosPendientes && soundEnabledRef.current) {
      playNewOrderBeep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hayPedidosPendientes]);

  // ── Aviso por orden individual al superar 10 minutos de espera ────────────
  // No usa un temporizador propio: se apoya en pendingOrders, que ya se
  // actualiza en cada ciclo de polling (~15s, POLLING_INTERVAL) y también de
  // inmediato al producir/agregar una orden. Anuncia cada orden pendiente una
  // sola vez por cada bloque de 10 minutos cruzado (10, 20, 30…), sin repetir
  // en cada revisión mientras siga en el mismo bloque.
  const overdueBucketRef = useRef(new Map()); // orderId -> último bloque de 10 min anunciado
  useEffect(() => {
    const currentIds = new Set();

    pendingOrders.forEach((order) => {
      currentIds.add(order.id);
      const mins = Math.floor((Date.now() - new Date(order.date_proceso).getTime()) / 60000);
      const bucket = Math.floor(mins / 10); // 0 = menos de 10 min
      if (bucket < 1) return;

      const lastBucket = overdueBucketRef.current.get(order.id) || 0;
      if (bucket <= lastBucket) return;

      // Coordinación de solo lectura con el recordatorio inteligente de arriba:
      // si esa misma orden y ese mismo bloque ya fueron anunciados por él,
      // se omite aquí para no duplicar el mensaje (no se modifica ese efecto).
      const yaAnunciadoPorRecordatorioInteligente =
        lastAnnouncedOldestIdRef.current === order.id &&
        lastAnnouncedBucketRef.current >= bucket;

      overdueBucketRef.current.set(order.id, bucket);
      if (!yaAnunciadoPorRecordatorioInteligente && soundEnabledRef.current) {
        const mesa = order.mesa ? `Mesa ${order.mesa}` : `Pedido ${order.numero_pedido}`;
        speak(`La orden de ${mesa} lleva ${mins} minutos en espera`, order.id);
      }
    });

    // Limpiar ids que ya no están pendientes (producidas o canceladas)
    overdueBucketRef.current.forEach((_, id) => {
      if (!currentIds.has(id)) overdueBucketRef.current.delete(id);
    });
  }, [pendingOrders, speak]);

  // Produce item
  const produceItem = useCallback(async (productId, option, productName, itemData) => {
    if (!productId || isProcessingRef.current || viewMode !== "pending") return;

    const estadoFinal = option === "cancelled" ? "void" : "Producido";
    isProcessingRef.current = true;
    setIsProducing(true); // Feedback visual inmediato

    // Optimistic update
    setPendingOrders(prev => prev.map(order => ({
      ...order,
      items: order.items.filter(item => item.id_producto !== productId)
    })).filter(order => order.items.length > 0));

    if (selectedProductId === productId) {
      setSelectedProductId(null);
      setSelectedOrderId(null);
    }

    try {
      await enviar_pedidokds({ id: productId, estado: estadoFinal }, null, property);
      await refetch();
    } catch (error) {
      console.error("Error:", error);
      await refetch();
    } finally {
      setTimeout(() => {
        isProcessingRef.current = false;
        setIsProducing(false); // Liberar estado visual
      }, 300); // Reducido de 500ms a 300ms para respuesta más rápida
    }
  }, [property, refetch, selectedProductId, viewMode]);

  // Revert functions
  const revertVoidToPending = useCallback(async (productId) => {
    if (!productId || isProcessingRef.current) return;
    isProcessingRef.current = true;
    // Optimistic: quitar de cancelados inmediatamente (sin esperar API)
    setVoidItems(prev => prev.filter(i => i.id_producto !== productId));
    setSelectedVoidId(null);
    setViewMode("pending");
    try {
      await enviar_pedidokds({ id: productId, estado: "pendiente" }, null, property);
      await refetch();
    } catch (error) {
      console.error("Error revirtiendo void:", error);
      await refetch(); // restaurar estado correcto en error
    } finally {
      setTimeout(() => { isProcessingRef.current = false; }, 500);
    }
  }, [property, refetch]);

  const revertProducedToPending = useCallback(async (productId) => {
    if (!productId || isProcessingRef.current) return;
    isProcessingRef.current = true;
    // Optimistic: quitar del historial inmediatamente
    setProducedItems(prev => prev.filter(i => i.id_producto !== productId));
    setSelectedHistoryId(null);
    setShowHistory(false);
    setViewMode("pending");
    try {
      await enviar_pedidokds({ id: productId, estado: "pendiente" }, null, property);
      await refetch();
    } catch (error) {
      console.error("Error revirtiendo producido:", error);
      await refetch(); // restaurar estado correcto en error
    } finally {
      setTimeout(() => { isProcessingRef.current = false; }, 500);
    }
  }, [property, refetch]);

  // Procesador de cola — consume un ítem a la vez, indicador = estado real de HTTP
  // try-finally garantiza que isQueueRunningRef.current siempre se libera,
  // incluyendo el caso de timeout en enviar_pedidokds (AbortError → catch interno → finally)
  const runQueue = useCallback(async () => {
    if (isQueueRunningRef.current) return; // ya hay un procesador activo
    isQueueRunningRef.current = true;

    try {
      while (processingQueueRef.current.length > 0) {
        const entry = processingQueueRef.current[0]; // ver el primero sin sacarlo aún

        // Indicador activo: hay una petición HTTP en curso
        setIsProducing(true);

        // Optimistic update: quitar este ítem del estado local inmediatamente
        setPendingOrders(prev =>
          prev.map(order => ({
            ...order,
            items: order.items.filter(item => item.id_producto !== entry.productId)
          })).filter(order => order.items.length > 0)
        );

        try {
          await enviar_pedidokds({ id: entry.productId, estado: "Producido" }, null, property);
        } catch (err) {
          console.error("Error produciendo ítem:", entry.productId, err);
          // El optimistic update ya lo quitó de la UI; el refetch al final corrige si hace falta
        }

        // Sacar de la cola solo después de que la petición terminó (éxito o error)
        processingQueueRef.current.shift();

        // Si aún quedan ítems, el while continúa inmediatamente (sin pausa artificial)
      }

      // Cola vacía: sincronizar con el servidor una sola vez
      await refetch();
    } finally {
      // Siempre liberar el guard y apagar el indicador — incluso en error inesperado
      setIsProducing(false);
      isQueueRunningRef.current = false;
    }
  }, [property, refetch]);

  // Produce selected — agrega a la cola y arranca el procesador si no está corriendo
  const produceSelected = useCallback(() => {
    if (selectedItems.size === 0 || viewMode !== "pending") return;

    const toProcess = Array.from(selectedItems.values());

    // Evitar duplicados: filtrar items que ya están en la cola
    const enqueuedIds = new Set(processingQueueRef.current.map(x => x.productId));
    const newItems = toProcess.filter(({ productId }) => !enqueuedIds.has(productId));

    if (newItems.length === 0) return; // todos ya estaban encolados

    // Agregar nuevos items al final de la cola
    processingQueueRef.current.push(...newItems);

    // Liberar la selección inmediatamente — el usuario puede seguir seleccionando
    setSelectedItems(new Map());
    setSelectedOrderId(null);
    setSelectedProductId(null);
    setKbFocusedProductId(null);

    // Arrancar el procesador (si ya estaba corriendo, los items nuevos se procesan solos)
    runQueue();
  }, [selectedItems, viewMode, runQueue]);

  // Navigation handlers
  // selectProduct ahora hace toggle multi-select en lugar de single-select
  const selectProduct = useCallback((orderId, productId) => {
    if (viewMode !== "pending") return;
    const order = tiempoOrders.find(o => o.id === orderId && !o.tiempoWaiting);
    if (!order) return;
    const item = order.items.find(i => i.id_producto === productId);
    if (!item) return;
    setSelectedItems(prev => {
      const next = new Map(prev);
      if (next.has(productId)) { next.delete(productId); }
      else { next.set(productId, { orderId, productId, item }); }
      return next;
    });
    // Mantener legacy para F6 (receta del último producto tocado)
    setSelectedOrderId(orderId);
    setSelectedProductId(productId);
  }, [viewMode, tiempoOrders]);

  const selectVoid = useCallback((voidId) => {
    if (viewMode !== "voids") return;
    setSelectedVoidId(voidId);
  }, [viewMode]);

  const selectHistoryItem = useCallback((historyId) => {
    if (viewMode !== "history") return;
    setSelectedHistoryId(historyId);
  }, [viewMode]);

  // UI Handlers
  const showVoids = useCallback(() => {
    setViewMode("voids");
    setShowHistory(false);
    setShowRecipe(false);
    setSelectedHistoryId(null);
    setSelectedItems(new Map());
    setKbFocusedProductId(null);
  }, []);

  const showHistoryPanel = useCallback(() => {
    setViewMode("history");
    setShowHistory(true);
    setShowRecipe(false);
    setSelectedHistoryId(null);
    setSelectedItems(new Map());
    setKbFocusedProductId(null);
  }, []);

  const closeHistoryPanel = useCallback(() => {
    setViewMode("pending");
    setShowHistory(false);
    setSelectedHistoryId(null);
  }, []);

  const showRecipePanel = useCallback((productId, productName) => {
    setRecipeProduct({ id: productId, name: productName });
    setShowRecipe(true);
  }, []);

  const closeRecipePanel = useCallback(() => {
    setShowRecipe(false);
    setRecipeProduct({ id: null, name: "" });
  }, []);

  const regresarAPendiente = useCallback(() => {
    if (viewMode === "voids" && selectedVoidId) {
      revertVoidToPending(selectedVoidId);
    } else if (viewMode === "history" && selectedHistoryId) {
      revertProducedToPending(selectedHistoryId);
    } else {
      setViewMode("pending");
      setShowHistory(false);
      setSelectedVoidId(null);
      setSelectedHistoryId(null);
    }
  }, [viewMode, selectedVoidId, selectedHistoryId, revertVoidToPending, revertProducedToPending]);

  // Fullscreen handler
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Toggle Library handler
  const toggleLibrary = useCallback(() => {
    setShowLibrary(prev => !prev);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Wake-up al volver a la pestaña o al monitor
  // Chrome puede congelar timers y requests cuando la pestaña está en segundo plano.
  // Al recuperar visibilidad: limpiamos cualquier guard que haya quedado bloqueado
  // por un timeout/error y disparamos un refetch inmediato para sincronizar.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      // Si la cola de producción quedó bloqueada (timeout de red), liberarla
      if (isQueueRunningRef.current) {
        console.warn("KDS wake-up: liberando isQueueRunningRef bloqueado");
        isQueueRunningRef.current = false;
        setIsProducing(false);
        processingQueueRef.current = []; // descartar ítems de cola anterior
      }

      // Refetch inmediato para mostrar el estado real del servidor
      refetch();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [refetch]);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeys = (e) => {
      const key = e.code;

      if (key === 'F1' && viewMode === "pending") {
        e.preventDefault();
        // Producir todos los ítems seleccionados (batch con Promise.all)
        produceSelected();
      }
      else if (key === 'F2') { e.preventDefault(); showVoids(); }
      else if (key === 'F3') { e.preventDefault(); regresarAPendiente(); }
      else if (key === 'F4') { e.preventDefault(); showHistoryPanel(); }
      else if (key === 'F5') { e.preventDefault(); refetch(); }
      else if (key === 'F6' && viewMode === "pending") {
        e.preventDefault();
        // Receta del último producto tocado (solo cuando hay 1 seleccionado)
        if (selectedItems.size === 1) {
          const entry = Array.from(selectedItems.values())[0];
          showRecipePanel(entry.item.Iditem, entry.item.nombre);
        } else if (selectedProductId) {
          const order = tiempoOrders.find(o => o.id === selectedOrderId);
          const item = order?.items.find(i => i.id_producto === selectedProductId);
          if (item) showRecipePanel(item.Iditem, item.nombre);
        }
      }
      else if (key === 'F7') {
        e.preventDefault();
        toggleLibrary();
      }
      else if (key === 'F11') { e.preventDefault(); toggleFullscreen(); }
      // Escape: primero limpiar selección, luego volver a pendientes
      else if (key === 'Escape') {
        if (showLibrary) {
          setShowLibrary(false);
        } else if (selectedItems.size > 0 || kbFocusedProductId) {
          setSelectedItems(new Map());
          setKbFocusedProductId(null);
        } else {
          setViewMode("pending");
          setShowHistory(false);
          setShowRecipe(false);
        }
      }
      // ── Navegación por teclado (flechas + espacio + ctrl+a) ──────────────
      else if (key === 'ArrowDown' && viewMode === "pending") {
        e.preventDefault();
        if (!navigableItems.length) return;
        if (!kbFocusedProductId) {
          setKbFocusedProductId(navigableItems[0].item.id_producto);
        } else {
          const idx = navigableItems.findIndex(x => x.item.id_producto === kbFocusedProductId);
          const next = idx < navigableItems.length - 1 ? idx + 1 : 0;
          setKbFocusedProductId(navigableItems[next].item.id_producto);
        }
      }
      else if (key === 'ArrowUp' && viewMode === "pending") {
        e.preventDefault();
        if (!navigableItems.length) return;
        if (!kbFocusedProductId) {
          setKbFocusedProductId(navigableItems[navigableItems.length - 1].item.id_producto);
        } else {
          const idx = navigableItems.findIndex(x => x.item.id_producto === kbFocusedProductId);
          const prev = idx <= 0 ? navigableItems.length - 1 : idx - 1;
          setKbFocusedProductId(navigableItems[prev].item.id_producto);
        }
      }
      // Espacio: toggle selección del ítem con foco de teclado
      else if (key === 'Space' && viewMode === "pending") {
        e.preventDefault();
        if (kbFocusedProductId) {
          const navItem = navigableItems.find(x => x.item.id_producto === kbFocusedProductId);
          if (navItem) selectProduct(navItem.orderId, navItem.item.id_producto);
        }
      }
      // Ctrl+A: seleccionar todos los ítems activos
      else if (key === 'KeyA' && e.ctrlKey && viewMode === "pending") {
        e.preventDefault();
        setSelectedItems(new Map(
          navigableItems.map(({ item, orderId }) => [item.id_producto, { orderId, productId: item.id_producto, item }])
        ));
      }
    };

    window.addEventListener('keydown', handleGlobalKeys);
    return () => window.removeEventListener('keydown', handleGlobalKeys);
  }, [
    viewMode, selectedOrderId, selectedProductId, tiempoOrders,
    produceSelected, regresarAPendiente, showHistoryPanel, showVoids,
    refetch, showRecipePanel, toggleFullscreen, toggleLibrary, showLibrary,
    selectedItems, navigableItems, kbFocusedProductId, selectProduct
  ]);

  // Periodic server status check + eventos nativos del navegador
  useEffect(() => {
    checkServerStatus();
    const interval = setInterval(checkServerStatus, 30000);
    // Reacción inmediata a cambios de conectividad de red
    const handleOnline  = () => checkServerStatus();
    const handleOffline = () => setServerStatus("offline");
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      clearInterval(interval);
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkServerStatus]);

  // Current selected item for display
  // Con multi-select: retorna el último ítem tocado (para receta, badge, etc.)
  const currentSelectedItem = useMemo(() => {
    if (viewMode !== "pending" || selectedItems.size === 0) return null;
    const entries = Array.from(selectedItems.values());
    return entries[entries.length - 1]?.item || null;
  }, [selectedItems, viewMode]);

  // pendingCount refleja el filtro de revenue activo (para el badge del stat-card)
  const pendingCount = (selectedRevenue
    ? pendingOrders.filter(o => decodeUnicode(o.revenue || "Restaurante") === selectedRevenue)
    : pendingOrders
  ).reduce((sum, order) => sum + (order.items?.length || 0), 0);

  const totalPendingCount = pendingOrders.reduce((sum, order) => sum + (order.items?.length || 0), 0);

  return (
    <div className={`kds-app ${isFullscreen ? 'fullscreen-mode' : ''}`}>
      {/* Header Moderno */}
      <header className="kds-header-modern">
        <div className="header-left">
          <div className="logo-area">
            <ChefHat size={28} />
            <h1>KDS <span className="kds-id">#{kdsId}</span></h1>
          </div>
          <div className="property-badge">{property || "Sin propiedad"}</div>
        </div>

        <div className="header-center">
          <div className="stats-container">
            <div className={`stat-card ${viewMode === 'pending' ? 'active' : ''}`} onClick={() => { setViewMode("pending"); setShowHistory(false); }}>
              <ClipboardList size={20} />
              <span className="stat-value">{pendingCount}</span>
              <span className="stat-label">Pendientes</span>
            </div>
            <div className={`stat-card ${viewMode === 'voids' ? 'active' : ''}`} onClick={showVoids}>
              <XCircle size={20} />
              <span className="stat-value">{voidItems.length}</span>
              <span className="stat-label">Cancelados</span>
            </div>
            <div className={`stat-card ${viewMode === 'history' ? 'active' : ''}`} onClick={showHistoryPanel}>
              <History size={20} />
              <span className="stat-value">{producedItems.length}</span>
              <span className="stat-label">Producidos</span>
            </div>
          </div>
        </div>

        <div className="header-right">
          <div className={`server-status status-${serverStatus}`}>
            <div className="status-dot"></div>
            <span>{serverStatus === "online" ? "En línea" : serverStatus === "offline" ? "Desconectado" : "Verificando..."}</span>
          </div>
          <button
            className={`btn-sound ${soundEnabled ? 'sound-on' : 'sound-off'}`}
            onClick={() => {
              const next = !soundEnabled;
              setSoundEnabled(next);
              if (next) {
                setTimeout(() => {
                  playBeep(660, 0.15, 0.3);
                  setTimeout(() => playBeep(880, 0.2, 0.3), 170);
                  setTimeout(() => {
                    if (window.speechSynthesis) {
                      const u = new SpeechSynthesisUtterance('Sonido activado');
                      u.lang = 'es-MX';
                      u.rate = 1;
                      window.speechSynthesis.speak(u);
                    }
                  }, 400);
                }, 50);
              }
            }}
            title={soundEnabled ? 'Silenciar sonido' : 'Activar sonido'}
          >
            {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </button>
          <button className="btn-fullscreen" onClick={toggleFullscreen}>
            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          </button>
        </div>
      </header>

      {/* ── Barra de Revenue/Centro de Consumo ────────────────────────────── */}
      {availableRevenues.length > 0 && (
        <div className="kds-revenue-bar">
          <button
            className={`kds-revenue-pill ${!selectedRevenue ? 'active' : ''}`}
            onClick={() => setSelectedRevenue(null)}
          >
            <span className="kds-rev-name">TODOS</span>
            <span className="kds-rev-count">{totalPendingCount}</span>
          </button>
          {availableRevenues.map(rev => (
            <button
              key={rev}
              className={`kds-revenue-pill ${selectedRevenue === rev ? 'active' : ''}`}
              onClick={() => setSelectedRevenue(prev => prev === rev ? null : rev)}
            >
              <span className="kds-rev-name">{rev.toUpperCase()}</span>
              <span className="kds-rev-count">{revenueCounts[rev] || 0}</span>
            </button>
          ))}
        </div>
      )}

      {/* Notification Toast */}
      {notificationCount > 0 && (
        <div className="notification-toast">
          <Bell size={18} />
          <span>{notificationCount} nueva(s) orden(es)</span>
        </div>
      )}

      {/* Main Content */}
      <main className="kds-main">
        {error && (
          <div className="error-banner-modern">
            <AlertTriangle size={20} />
            <span>Sin conexión — esperando servidor</span>
            <button onClick={refetch} className="btn-retry">Reintentar</button>
          </div>
        )}

        {/* Pending Orders View */}
        {viewMode === "pending" && !showRecipe && (
          <div className="orders-container">
            {loading && pendingOrders.length === 0 ? (
              <div className="loading-container">
                <div className="spinner-large"></div>
                <p>Cargando órdenes...</p>
              </div>
            ) : filteredTiempoOrders.length === 0 ? (
              <div className="empty-state-modern">
                <UtensilsCrossed size={64} strokeWidth={1.5} />
                {selectedRevenue ? (
                  <>
                    <h3>Sin órdenes en {selectedRevenue}</h3>
                    <p>No hay pedidos pendientes para este centro de consumo</p>
                  </>
                ) : (
                  <>
                    <h3>Sin órdenes pendientes</h3>
                    <p>Esperando nuevos pedidos del POS</p>
                  </>
                )}
              </div>
            ) : (
              <div className="orders-grid-modern">
                {filteredTiempoOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    selectedProductIds={selectedProductIds}
                    kbFocusedProductId={kbFocusedProductId}
                    onSelectProduct={selectProduct}
                    onShowRecipe={showRecipePanel}
                    orderStatus={order.tiempoWaiting ? "waiting" : "pending"}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Voids View */}
        {viewMode === "voids" && !showRecipe && (
          <div className="voids-container-modern">
            <div className="voids-header-modern">
              <XCircle size={28} />
              <h2>Productos Cancelados</h2>
              <small>Últimos 10 registros</small>
            </div>
            <div className="voids-grid">
              {voidItems.length === 0 ? (
                <div className="empty-state-modern">
                  <p>No hay cancelaciones recientes</p>
                </div>
              ) : (
                voidItems.map((item) => (
                  <div
                    key={item.id_producto}
                    className={`void-card ${selectedVoidId === item.id_producto ? 'selected' : ''}`}
                    onClick={() => selectVoid(item.id_producto)}
                    onDoubleClick={() => revertVoidToPending(item.id_producto)}
                  >
                    <div className="void-card-header">
                      <span className="void-icon">❌</span>
                      <span className="void-name">{decodeUnicode(item.nombre)}</span>
                      <span className="void-quantity">x{Math.abs(item.cantidad)}</span>
                    </div>
                    <div className="void-card-details">
                      <span className="void-reason">{item.void_name || "Cancelado"}</span>
                      <span className="void-time">{new Date(item.fecha_cancelacion).toLocaleTimeString()}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* History View */}
        {viewMode === "history" && !showRecipe && (
          <div className="history-panel-modern">
            <div className="history-header-modern">
              <History size={24} />
              <h2>Historial de Producción</h2>
              <button onClick={closeHistoryPanel} className="btn-close-history">
                <XCircle size={20} /> Cerrar
              </button>
            </div>
            <div className="history-list-modern">
              {producedItems.length === 0 ? (
                <div className="empty-state-modern">
                  <p>No hay productos producidos</p>
                </div>
              ) : (
                producedItems.map((item) => (
                  <div
                    key={item.id_producto}
                    className={`history-card ${selectedHistoryId === item.id_producto ? 'selected' : ''}`}
                    onClick={() => selectHistoryItem(item.id_producto)}
                    onDoubleClick={() => revertProducedToPending(item.id_producto)}
                  >
                    <div className="history-card-left">
                      <CheckCircle2 size={20} className="success-icon" />
                      <div className="history-info">
                        <span className="history-name">{decodeUnicode(item.nombre)}</span>
                        <span className="history-meta">Mesa {item.mesa} • {item.cliente}</span>
                      </div>
                    </div>
                    <div className="history-card-right">
                      <span className="history-time">{item.fecha_proceso}</span>
                      <button
                        className="btn-revert-history"
                        onClick={(e) => {
                          e.stopPropagation();
                          revertProducedToPending(item.id_producto);
                        }}
                        title="Regresar a pendiente"
                      >
                        <RotateCcw size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Recipe Panel Modal */}
        {showRecipe && (
          <RecipePanel
            productId={recipeProduct.id}
            productName={recipeProduct.name}
            onClose={closeRecipePanel}
            property={property}
          />
        )}
      </main>

      {/* Biblioteca de Referencia */}
      <KDSBiblioteca
        isOpen={showLibrary}
        onClose={toggleLibrary}
        property={property}
      />

      {/* Bump Bar */}
      <BumpBar
        onProduce={produceSelected}
        onShowVoids={showVoids}
        onShowHistory={showHistoryPanel}
        onReload={refetch}
        onBackToPending={regresarAPendiente}
        onShowRecipe={showRecipePanel}
        selectedItem={currentSelectedItem}
        selectionCount={selectedItems.size}
        voidsCount={voidItems.length}
        historyCount={producedItems.length}
        viewMode={viewMode}
        hasRecipe={true}
        onToggleFullscreen={toggleFullscreen}
        isFullscreen={isFullscreen}
        onShowLibrary={toggleLibrary}
        isProducing={isProducing}
      />
    </div>
  );
};

KDS.propTypes = {
  kdsId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default KDS;
